import { Routes } from '@angular/router';
import { HomePageComponent } from './home-page/containers/home-page/home-page.component';
import { SettingsPageComponent } from './modules/settings-page/containers/settings-page/settings-page.component';
import { LoginGuard } from './modules/auth0/guards/login.guard';
import { FeatureBit } from './modules/auth0/models/feature-bits.model';
import { FeatureBitsGuard } from './modules/auth0/guards/feature-bits.guard';
import { SignupAccessGuard } from './modules/auth0/guards/signup-access.guard';
import { CitationsPageComponent } from './home-page/containers/citations-page/citations-page.component';
import { SIGGuard } from './modules/auth0/guards/sig.guard';
import { HomePageInitParamsResolver } from './home-page/containers/home-page/home-page-init-params.resolver';
import { AboutFranklinComponent } from './static-pages/about-franklin/about-franklin.component';
import { IconsDirectoryComponent } from './dev-module/modules/icons-directory/icons-directory.component';
import { environment } from '../environments/environment';

let devRoutes: Routes = [];
if (!environment.production) {
  devRoutes = [
    {
      path: 'icons',
      component: IconsDirectoryComponent,
    },
  ];
}
/*
 * Notes:
 * - No need to use LoginGuard alongside FeatureBit, since FeatureBit calls LoginGuard before validating the feature bits
 * */
export const routes: Routes = [
  {
    path: 'analysis-tool',
    loadChildren: () => import('./modules/analysis/analysis.routes').then((m) => m.ANALYSIS_ROUTES),
  },
  {
    path: 'clinical-db',
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'home' },
      { path: 'home', component: HomePageComponent, resolve: { params: HomePageInitParamsResolver } },
      {
        path: 'variant/:variantType/:variantId',
        loadChildren: () => import('./modules/variant-page/variant-page.routes').then((m) => m.VARIANT_PAGE_ROUTES),
      },
      {
        path: 'variant/:variantId',
        redirectTo: 'variant/snp/:variantId',
      },
      {
        path: 'gene/:referenceVersion/:geneSymbol',
        loadChildren: () => import('./modules/gene-page/gene-page.routes').then((m) => m.GENE_PAGE_ROUTES),
      },
      {
        path: 'gene/:geneSymbol',
        redirectTo: 'gene/hg19/:geneSymbol',
      },
      {
        path: 'knowledge-base',
        loadChildren: () => import('./knowledge-base/knowledge-base.routes').then((m) => m.KNOWLEDGE_BASE_ROUTES),
      },
      {
        path: 'discovery',
        loadChildren: () => import('./modules/discovery/discovery.routes').then((m) => m.DISCOVERY_ROUTES),
        canActivate: [FeatureBitsGuard],
        data: { featureBit: FeatureBit.Discovery },
      },
      {
        path: 'assets',
        loadChildren: () => import('./modules/assets-page/assets-page.routes').then((m) => m.ASSETS_PAGE_ROUTES),
      },
      {
        path: 'sig/:sigId',
        loadChildren: () => import('./modules/sig/sig.routes').then((m) => m.SIG_ROUTES),
        canActivate: [SIGGuard],
      },
    ],
  },
  {
    path: 'variant/:variantType/:variantId',
    pathMatch: 'full',
    redirectTo: 'clinical-db/variant/:variantType/:variantId',
  },
  { path: 'variant/:variantId', pathMatch: 'full', redirectTo: 'clinical-db/variant/snp/:variantId' },
  { path: 'my-variants', pathMatch: 'full', redirectTo: 'clinical-db/knowledge-base/variants' },
  {
    path: 'settings',
    canActivate: [LoginGuard],
    children: [
      { path: '', redirectTo: 'profile', pathMatch: 'full' },
      { path: ':tab', component: SettingsPageComponent },
    ],
  },
  {
    path: 'credit-report',
    loadComponent: () =>
      import('./modules/credit-report/containers/credit-report/credit-report.component').then(
        (m) => m.CreditReportComponent,
      ),
    canActivate: [LoginGuard, FeatureBitsGuard],
    data: { featureBitOrList: [FeatureBit.CreditReport] },
  },
  {
    path: 'post-join',
    canActivate: [LoginGuard],
    loadComponent: () =>
      import('./post-join-page/containers/post-join-page/post-join-page.component').then(
        (m) => m.PostJoinPageComponent,
      ),
  },
  {
    path: 'join',
    loadComponent: () =>
      import('./join-page/containers/join-page/join-page.component').then((m) => m.JoinPageComponent),
    canActivate: [SignupAccessGuard],
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./login-page/containers/login-page/login-page.component').then((m) => m.LoginPageComponent),
  },
  {
    path: 'reset',
    loadComponent: () =>
      import('./reset-password/containers/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'samples',
    loadChildren: () => import('./modules/samples/samples.routes').then((m) => m.SAMPLES_ROUTES),
    canActivate: [FeatureBitsGuard],
    data: { featureBitOrList: [FeatureBit.Assets, FeatureBit.Samples] },
  },
  {
    path: 'variant',
    loadChildren: () => import('./modules/variant-page/variant-page.routes').then((m) => m.VARIANT_PAGE_ROUTES),
    outlet: 'dialog',
  },
  {
    path: 'new-case',
    loadChildren: () => import('./modules/case-dialogs/case-dialogs.routes').then((m) => m.CASE_DIALOGS_ROUTES),
    outlet: 'dialog',
  },
  {
    path: 'edit-case',
    loadChildren: () => import('./modules/case-dialogs/case-dialogs.routes').then((m) => m.CASE_DIALOGS_ROUTES),
    outlet: 'dialog',
  },
  {
    path: 'citations',
    component: CitationsPageComponent,
  },
  { path: 'about', pathMatch: 'full', redirectTo: 'about-franklin' },
  {
    path: 'about-franklin',
    component: AboutFranklinComponent,
  },
  {
    path: 'gene',
    loadChildren: () => import('./modules/gene-page/gene-page.routes').then((m) => m.GENE_PAGE_ROUTES),
    outlet: 'dialog',
  },
  {
    path: 'whole-genome',
    loadChildren: () =>
      import('./modules/whole-genome-page/whole-genome-page.routes').then((m) => m.WHOLE_GENOME_PAGE_ROUTES),
    outlet: 'dialog',
  },
  ...devRoutes,
  { path: '', pathMatch: 'full', redirectTo: 'clinical-db' },
  { path: '**', redirectTo: 'clinical-db' },
];
